/**
 * Determines if product buttons are currently visible in the viewport
 * @param {HTMLElement} productButtons - The product buttons element
 * @returns {boolean} Whether the product buttons are visible
 */
const isProductButtonsVisible = (productButtons) => {
  const rect = productButtons.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const scrollTop = window.scrollY;

  if(document.body.classList.contains('overflow-hidden')) return true;
  const loyaltyElement = document.querySelectorAll('[id^="shopify-block-toki_loyalty_rewards"]');

  const loyaltyGatingElement = document.querySelectorAll('[id^="toki-shopify-integration-block-gating-root"]');
  if(loyaltyElement.length > 0 || loyaltyGatingElement.length > 0) return true;

  return (windowHeight + scrollTop <= rect.bottom) || 
         (rect.top >= 0 && rect.bottom <= windowHeight + scrollTop);
};

/**
 * Checks if footer is at least 50% visible in the viewport
 * @param {HTMLElement} footer - The footer element
 * @returns {boolean} Whether footer is 50% visible
 */
const isFooterFiftyPercentVisible = (footer) => {
  const rect = footer.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const visibleHeight = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
  return visibleHeight >= windowHeight / 2;
};

/**
 * Manages visibility of sticky variant selector and chat button on scroll
 */
const handleScrollVisibility = () => {
  const productButtons = document.querySelector('.product-form__buttons');
  const variantStickySelector = document.getElementById('variant-selects-sticky-container');
  const buttonChat = document.getElementById('chat-button');
  const footer = document.querySelector('footer');

  if (!productButtons || !variantStickySelector) return;

  const shouldShowSticky = !isProductButtonsVisible(productButtons);
  const shouldHideOnFooter = isFooterFiftyPercentVisible(footer);

  variantStickySelector.classList.toggle('isVisible', shouldShowSticky && !shouldHideOnFooter);
  if (buttonChat) {
    buttonChat.classList.toggle('stickyAtc-isVisible', shouldShowSticky && !shouldHideOnFooter);
  }
};

/**
 * Removes the variant sticky selector from the DOM
 */
const removeVariantStickySelector = () => {
  const variantStickySelector = document.getElementById('variant-selects-sticky-container');
  variantStickySelector?.remove();
};

/**
 * Creates dynamic styles for chat button positioning
 * @param {HTMLElement} mainContainer - The main content container
 */
const createStyleChatButtonPosition = (mainContainer) => {
  const stickyContainer = document.getElementById('variant-selects-sticky-container');
  if (!stickyContainer) return;

  const stickyContainerHeight = stickyContainer.clientHeight;
  if (stickyContainerHeight === 0) {
    setTimeout(() => createStyleChatButtonPosition(mainContainer), 100);
    return;
  }

  const existingStyle = mainContainer.querySelector('#chat-button-sticky-style');
  existingStyle?.remove();

  const style = document.createElement('style');
  style.id = 'chat-button-sticky-style';
  style.innerHTML = `
    #chat-button.stickyAtc-isVisible {
      bottom: ${stickyContainerHeight + 20}px !important;
    }
  `;
  mainContainer.appendChild(style);
};

/**
 * Creates CSS styles for sticky selector
 * @param {HTMLElement} mainContainer - The main content container
 */
const createStyleStickySelector = (mainContainer) => {
  const stickyStyle = mainContainer.querySelector('#variant-selects-sticky-style');
  if (stickyStyle) return;
  const style = document.createElement('style');
  style.id = 'variant-selects-sticky-style';
  style.innerHTML = `
    .variant-selects-sticky-mobile-container {
      box-shadow: 0 0 10px rgba(0,0,0,0.3);
      position: sticky;
      bottom: 0;
      z-index: 999999999;
      background-color: white;      
      display: none;
      opacity: 0;
      transition: opacity 0.5s;
    }
    .variant-selects-sticky-mobile-container.isVisible {
      animation: fadeIn 0.5s;
      opacity: 1;
      display: flex;
      padding: 12px;
      flex-direction: column;
    }
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .variant-selects-sticky-mobile-container .variant-selects-sticky-mobile-container-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }

    .variant-selects-sticky-mobile-container .variant-selects-sticky-mobile-container-header h3 {
      margin: 0;
    }

    .variant-selects-sticky-mobile-container-header__title {
      display: flex;
      flex-direction: column;
    }

    .variant-selects-sticky-mobile-container-header__title .sticky-size-selected__container {
      margin: 0px;
    }

    .variant-selects-sticky-mobile-container-header .sticky-price__container.price--on-sale .price__sale {
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
    }

    .variant-selects-sticky-mobile-container-header .sticky-price__container span {
      line-height: 1;
    }

    .variant-selects-sticky-mobile-container-header .sticky-price__container:not(.price--on-sale) .price-item.price-item--regular {
      color: #0D0C22;
    }

    .variant-selects-sticky-mobile-container-header .sticky-price__container .price-item.price-item--regular {
      font-family: 'Alternate-Gothic-ATF';
      font-size: 14px;
      font-style: normal;
      font-weight: 350;
      line-height: normal;
    }

    .variant-selects-sticky-mobile-container-header .sticky-price__container .price-item.price-item--sale {

      font-family: 'Alternate-Gothic-ATF';
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky fieldset {
      margin: 0;
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      gap: 4px;
      align-self: stretch;
      flex-wrap: wrap;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky fieldset .product-form__legend-sizing-guide-wrapper {
      width: 100%;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky fieldset .product-form__legend-sizing-guide-wrapper modal-opener{
      display: none;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky fieldset label{
      margin: 0;
      min-width: 46px;
      width: auto;
      height: 32px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      line-height: normal;
      opacity: 1;
      padding: 11px 15px;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky fieldset label.not-clickable {
      opacity: 0.5;
    }

    .variant-selects-sticky-mobile-container #variant-selects-sticky .is-sold-out {
      border: 0.6px solid #E9E9E9;
      color: #DADADA;
    }

    .variant-selects-sticky-mobile-container-footer__button {
      font-size: 16px;
      margin-top: 12px;
    }

    .variant-selects-sticky-mobile-container-footer__button.is-sold-out {
      opacity: 0.5;
    }
  `;
  mainContainer.appendChild(style);
  createStyleChatButtonPosition(mainContainer); 
}

/**
* Creates a cloned variant selector for mobile
*/
const createVariantSelectorCloned = () => {

  const mainContainer = document.getElementById('MainContent');

  const variantOriginalSelector = mainContainer?.querySelector('[id^="variant-selects-template--"]');
  if (!variantOriginalSelector) return;

  const variantStickySelector = mainContainer?.querySelector('#variant-selects-sticky');
  if(variantStickySelector) variantStickySelector.remove();

  const variantSelectorCloned = variantOriginalSelector.cloneNode(true);
  if (!variantSelectorCloned) return;

  const clonedSizeSelectedWrapper = variantSelectorCloned.querySelector('.product-form__legend-sizing-guide-wrapper');
  if(clonedSizeSelectedWrapper) clonedSizeSelectedWrapper.remove();

  variantSelectorCloned.id = 'variant-selects-sticky';
  const variantsSelectors = variantSelectorCloned.querySelectorAll('input[type="radio"]');
  variantsSelectors.forEach((selector) => {
    if(selector.id) selector.id = `${selector.id}-sticky`;
    if(selector.name) selector.name = `${selector.name}-sticky`;
  });
  variantSelectorCloned.querySelectorAll('label').forEach((label) => {
    const originalFor = label.htmlFor;
    if(originalFor) {
      label.htmlFor = label.htmlFor + '-sticky';
      label.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if(event.target.classList.contains('not-clickable')) return;
        disableToggleVariantsSelectors(true);
        handleChangeClonedVariant(originalFor, label.htmlFor, event);
      }); 
    }
  });

  const headSizeSelectedWrapper = variantOriginalSelector.querySelector('.product-form__legend-sizing-guide-wrapper legend');
  if (headSizeSelectedWrapper) {
    const stickySizeSelectedContainer = document.querySelector('.sticky-size-selected__container');
    if (stickySizeSelectedContainer) {
      stickySizeSelectedContainer.innerHTML = headSizeSelectedWrapper.outerHTML;
    }
  }

  const stickyElementContainer = mainContainer.querySelector('#variant-selects-sticky-container');
  if (!stickyElementContainer) return;
  stickyElementContainer.insertBefore(variantSelectorCloned, stickyElementContainer.lastChild);
  createStyleChatButtonPosition(mainContainer); 
}

/**
 * Updates product information in sticky container
 */
const handleChangeProductInfo = () => {
  const mainContainer = document.getElementById('MainContent');
  if (!mainContainer) return;

  const stickyContainer = mainContainer.querySelector('#variant-selects-sticky-container');

  if (!stickyContainer) return;

  const stickyElementContainerFooter = stickyContainer.querySelector('.variant-selects-sticky-mobile-container-footer');
  const addToCartButtonOriginal = mainContainer.querySelector('[id^="ProductSubmitButton-template--"]');
  const stickyATcButton = stickyElementContainerFooter.querySelector('.variant-selects-sticky-mobile-container-footer__button');
  if(stickyATcButton) stickyATcButton.remove();

  if(addToCartButtonOriginal) {
    const addToCartButton = document.createElement('div');
    if (addToCartButtonOriginal.disabled) {
        addToCartButton.className = 'variant-selects-sticky-mobile-container-footer__button button button--full-width button--secondary is-sold-out';
        addToCartButton.textContent = 'Sold Out';
    } else {
        addToCartButton.className = 'variant-selects-sticky-mobile-container-footer__button button button--full-width button--primary';
        addToCartButton.textContent = 'Add to cart';

        addToCartButton.addEventListener('click', () => {
          if (addToCartButtonOriginal) addToCartButtonOriginal.click();
        });
    }
    stickyElementContainerFooter.appendChild(addToCartButton);
    disableToggleVariantsSelectors();
  }

  const priceContainer = mainContainer.querySelector('product-info .price');
  if (!priceContainer) return;

  // Remove existing price container
  const existingPriceContainer = stickyContainer.querySelector('.sticky-price__container');
  existingPriceContainer?.remove();

  // Clone and modify price container
  const priceContainerCloned = priceContainer.cloneNode(true);
  priceContainerCloned.classList.remove('price');
  priceContainerCloned.classList.add('sticky-price__container');

  const directSpans = priceContainerCloned.querySelectorAll(':scope > span');
  directSpans.forEach(span => span.remove());

  const stickyContainerHeaderPrice = stickyContainer.querySelector('.variant-selects-sticky-mobile-container-header');
  stickyContainerHeaderPrice.appendChild(priceContainerCloned);

  createVariantSelectorCloned();
};

/**
 * Toggles the disabled state of variant selectors within the sticky container.
 */
const disableToggleVariantsSelectors = (disable=false) => {
  const variantStickySelector = document.getElementById('variant-selects-sticky-container');
  if (!variantStickySelector) return;

  const variantInput = variantStickySelector.querySelectorAll('input[type="radio"]');

  for (const input of variantInput) {
    const inputId = input.id;
    if (!inputId) continue;

    const inputLabel = variantStickySelector.querySelector(`label[for="${inputId}"]`);
    if (!inputLabel) continue;

    input.classList.toggle('not-clickable', disable);
    inputLabel.classList.toggle('not-clickable', disable);
  }
};

/**
* Handles the change of the cloned variant selector
* @param {string} originalId - The original variant selector id
* @param {string} clonedId - The cloned variant selector id
* @param {Event} event - The event object
*/
const handleChangeClonedVariant = (originalId, clonedId) => {
  const inputOriginalElement = document.querySelector(`#${originalId}`);
  const inputElementCloned = document.querySelector(`#${clonedId}`);
  if (!inputOriginalElement || !inputElementCloned) return;

  inputOriginalElement.checked = true;
  inputElementCloned.checked = true;

  const mainContainer = document.getElementById('MainContent');
  const variantOriginalSelector = mainContainer?.querySelector('[id^="variant-selects-template--"]');

  if(inputOriginalElement.classList.contains('disabled')) {
    const labelInputOriginalElement = document.querySelector(`label[for="${originalId}"]`);
    if(labelInputOriginalElement) labelInputOriginalElement.click();
    setTimeout(handleChangeProductInfo, 300);
  }

  variantOriginalSelector.updateOptions();
  variantOriginalSelector.updateMasterId();
  variantOriginalSelector.updateSelectedSwatchValue({target:inputOriginalElement});
  variantOriginalSelector.toggleAddButton(true, '', false);
  variantOriginalSelector.updatePickupAvailability();
  variantOriginalSelector.removeErrorMessage();
  variantOriginalSelector.updateVariantStatuses();

  if (!variantOriginalSelector.currentVariant) {
    variantOriginalSelector.toggleAddButton(true, '', true);
    variantOriginalSelector.setUnavailable();
  } else {
    variantOriginalSelector.updateURL();
    variantOriginalSelector.updateVariantInput();
    variantOriginalSelector.renderProductInfo();
    variantOriginalSelector.updateShareUrl();
  }
  setTimeout(handleChangeProductInfo, 300);
}

/**
 * Creates a sticky variant selector for mobile
 * @param {HTMLElement} mainContainer - The main content container
 */
const createVariantStickySelector = (mainContainer) => {
  const variantStickySelector = document.getElementById('variant-selects-sticky-container');
  if (variantStickySelector) {
    createStyleChatButtonPosition(mainContainer); 
    return;
  };
  
  const stickyElementContainer = document.createElement('div');
  stickyElementContainer.id = 'variant-selects-sticky-container';
  stickyElementContainer.classList.add('variant-selects-sticky-mobile-container');

  const stickyElementContainerHeader = document.createElement('div');
  stickyElementContainerHeader.classList.add('variant-selects-sticky-mobile-container-header');
  const productTitle = mainContainer.querySelector('.product__title h1').textContent;

  stickyElementContainerHeader.innerHTML = `
      <div class="variant-selects-sticky-mobile-container-header__title">
        <h3>${productTitle}</h3>
        <div class="sticky-size-selected__container"></div>
      </div>
  `;

  stickyElementContainer.appendChild(stickyElementContainerHeader);

  const stickyElementContainerFooter = document.createElement('div');
  stickyElementContainerFooter.classList.add('variant-selects-sticky-mobile-container-footer');
  stickyElementContainer.appendChild(stickyElementContainerFooter);

  mainContainer.appendChild(stickyElementContainer);
  createStyleStickySelector(mainContainer);
  handleScrollVisibility();
  setTimeout(() => {
    handleChangeProductInfo();
  }, 300);
}

/**
 * Initializes sticky add to cart functionality
 */
const initStickyAddToCart = () => {
  const mainContainer = document.getElementById('MainContent');
  if (!mainContainer) return;

  const isMobile = window.innerWidth < 750;
  
  if (isMobile) {
    createVariantStickySelector(mainContainer);
    if (!window.scrollListenerAttached) {
      window.addEventListener('scroll', handleScrollVisibility);
      window.scrollListenerAttached = true;
    }
  } else {
    removeVariantStickySelector();
    if (window.scrollListenerAttached) {
      window.removeEventListener('scroll', handleScrollVisibility);
      window.scrollListenerAttached = false;
    }
  }
};

/**
* Handles the click event on menu mobile buttons
*/
const handleMenuMobileButtons = () => {

  const openMenuMobile = document.querySelector('header-drawer #Details-menu-drawer-container');
  if (openMenuMobile) {
    openMenuMobile.addEventListener('click', () => {
      const variantStickySelector = document.getElementById('variant-selects-sticky-container');
      const buttonChat = document.getElementById('chat-button');
      if (!variantStickySelector) return;

      setTimeout(() => {
        const showStickyElement = document.querySelector('header-drawer #Details-menu-drawer-container').classList.contains('menu-opening');
        variantStickySelector.classList.toggle('isVisible', !showStickyElement);
        if (buttonChat) {
          buttonChat.classList.toggle('stickyAtc-isVisible', !showStickyElement);
        }
      },100);  
    });
  }
}

// Initialize on DOM content loaded
window.addEventListener('load', () => {
  console.log('PDM-mobile-sticky-atc loaded');
    initStickyAddToCart();
    
    window.addEventListener('resize', () => {
      const newWidth = window.innerWidth;
      if (newWidth !== window.actualWidth) {
        window.actualWidth = newWidth;
        initStickyAddToCart();
      }
    });

    document.addEventListener('variantChangeProductInfo', () => {
      setTimeout(handleChangeProductInfo, 300);
    });

    document.addEventListener('variantChange', () => {
      setTimeout(handleChangeProductInfo, 300);
    });

    handleMenuMobileButtons();

});